import { Typography, styled } from '@mui/material';
import type { IAppDetailsAppData, IPlatform } from '../common-types';
import {
  mapAccreditationTooltipText,
  platformNameConverter,
} from '../common-utilities';
import { ScoreSummary } from '../AppDetails';
import { AccreditationBadge } from '@orcha/micro-library-core-ui';
import type { ImageProps } from 'next/image';

export type PlatformScoreSummaryProps = IPlatform & {
  app: IAppDetailsAppData;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  translatedText: {
    platformScoreSummaryHeading: string;
    accreditationBadgeTooltipText: string;
    platformScoreSummaryLevelText: {
      [key: string]: string;
    };
  };
};

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
}));

/**
 * PlatformScoreSummary from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param app is required, this is the app data that is passed to the component.
 * @param translatedText is required, this is the text that as already been translated by the consumer of this component.
 * @param FlexibleImageComponent is required, this is the component that is used to display the image.
 * @returns
 */

export const PlatformScoreSummary = ({
  platformName,
  app,
  translatedText,
  FlexibleImageComponent,
  ...props
}: PlatformScoreSummaryProps) => {
  const { platforms } = app ?? {};
  const relevantPlatform = platforms?.find(
    (p) => platformNameConverter(p.platformName) === platformName
  );
  const scoreSummary = relevantPlatform?.scoreSummary ?? undefined;
  const scoreSections = relevantPlatform?.scoreSections ?? [];
  return (
    <>
      {scoreSummary && (
        <Container {...props}>
          <Typography component="h2" variant="h6">
            {translatedText?.platformScoreSummaryHeading}
          </Typography>
          <ScoreSummary
            scoreSummary={{
              score: scoreSummary.scoreValue,
              scoreSupportingText: scoreSummary.level
                ? translatedText?.platformScoreSummaryLevelText?.[
                    scoreSummary.level
                  ]
                : undefined,
              accreditationLogo: scoreSummary.accreditation ? (
                <AccreditationBadge
                  src={scoreSummary.accreditation.logoUrl ?? ''}
                  FlexibleImageComponent={FlexibleImageComponent}
                  translatedText={{
                    tooltipText: mapAccreditationTooltipText(
                      translatedText?.accreditationBadgeTooltipText,
                      scoreSummary.accreditation.name
                    ),
                  }}
                  height={70}
                  width={70}
                />
              ) : undefined,
            }}
            scoreSections={scoreSections
              ?.filter((section) => section.name && section.scoreValue)
              ?.map((section) => {
                return {
                  name: section.name ?? '',
                  score: section.scoreValue ?? 0,
                };
              })}
          />
        </Container>
      )}
    </>
  );
};
